/**
 * SA-717 (spec §5): the Triage panel's sentences. The server words the parts
 * that need judgment (how a decision was made, an error code's words, who
 * decided); this module only assembles them. A code or an id is never shown:
 * an outcome this page does not know reads "Unknown outcome".
 */
import type { ThreadRecord, TriageRow, TriageRun } from '@/types/inbox'
import type { EmailRowMessage } from './emailRows'
import type { NeedsChoice } from './triageBlocks'
import { INTENT_TEXT } from '@/components/process/processEventText'
import { outcomeWords, whenText } from '@/components/process/stepLiveText'

/** What a handler did, by outcome. Exported: the metrics panel reads this one
 * map rather than keeping its own. */
export const OUTCOME_WORDS: Record<string, string> = {
  done: 'Done',
  pending: 'Waiting on a follow-up',
  skipped: 'Nothing was done',
  failed: 'The handler did not finish',
  running: 'Running',
  rejected: 'Closed as not an enquiry',
}

const how = (words: string | null) => (words ? ` (${words})` : '')
const by = (name: string | null) => (name ? ` by ${name}` : '')

/** SA-770: a message whose record was rejected as not an enquiry: who closed
 * it and when. The time is `whenText`'s hand-formatted reading, in the
 * viewer's zone unless `zone` names one. */
export function rejectedHeadline(row: TriageRow, now: Date = new Date(), zone?: string): string {
  const when = whenText(row.decidedAt, now, zone)
  const who = row.decidedByName ? ` by ${row.decidedByName}` : ''
  const at = when ? `${row.decidedByName ? ',' : ''} ${when}` : ''
  return `Not an enquiry: closed${who}${at}.`
}

export function rowHeadline(row: TriageRow, now?: Date, zone?: string): string {
  if (row.handlerOutcome === 'rejected') return rejectedHeadline(row, now, zone)
  const label = row.kindLabel ?? row.kind ?? 'Unknown'
  if (row.decisionStatus === 'proposed') {
    if (row.kind === 'unknown') {
      return row.proposedLabel ? `Needs a kind. Closest: ${row.proposedLabel}${how(row.proposedHow)}.` : 'Needs a kind.'
    }
    return `Proposed: ${label}${how(row.proposedHow)}.`
  }
  if (row.decisionStatus === 'approved') return `${label}. Approved${by(row.decidedByName)}.`
  if (row.decisionStatus === 'changed') return `${label}. Changed${by(row.decidedByName)}.`
  return `${label}${how(row.decidedHow)}.`
}

/** Why triage could not classify the message, while it still waits. */
export function notClassifiedLine(row: TriageRow): string | null {
  if (row.decisionStatus !== 'proposed' || row.kind !== 'unknown' || !row.errorWords) return null
  return `Not classified: ${row.errorWords}.`
}

/** The handler's own sentence, as the staff note says it. */
export function wouldSentence(would: string | null | undefined): string | null {
  return would ? `Approving would ${would}.` : null
}

export function kindSentence(would: string): string | null {
  return would ? `Deciding this kind would ${would}.` : null
}

/** SA-795: the panel's actions that hold a request while they work. */
export type TriageAction = 'approve' | 'apply' | 'not_for_us' | 'reject' | 'reinstate' | 'rerun' | 'update' | 'create' | 'accept_update' | 'close'

/** SA-795 (D4): the pressed button's label while its request is held. */
export const WORKING_LABEL: Record<TriageAction, string> = {
  approve: 'Approving…',
  apply: 'Applying…',
  not_for_us: 'Dismissing…',
  reject: 'Closing…',
  reinstate: 'Reinstating…',
  rerun: 'Re-running…',
  update: 'Updating…',
  create: 'Creating…',
  accept_update: 'Accepting…',
  close: 'Closing…',
}

/** SA-808: the words each action's working line needs. `would` is the
 * handler's sentence, the end of "Approving would …" (empty or absent = the
 * server sent none); `label` is the kind picked under Change kind; `record`
 * is the record as the confirms name it: "<noun> <number>", or "this
 * thread's <noun>". */
export interface WorkingWords {
  approve: { would?: string | null }
  apply: { would?: string | null; label: string }
  not_for_us: { would?: string | null }
  rerun: { would?: string | null }
  reject: { record: string }
  reinstate: { record: string }
  update: { record: string }
  create: { label: string; would?: string | null }
  accept_update: { record: string }
  close: { record: string }
}

const MINUTE = ' This can take up to a minute.'
const will = (would: string | null | undefined) => (would ? `: this will ${would}.` : '.')

/** One sentence per action, each typed by the words it needs. */
const WORKING: { [A in TriageAction]: (words: WorkingWords[A]) => string } = {
  approve: ({ would }) => `Approving${will(would)}`,
  apply: ({ would, label }) => `Applying ${label}${will(would)}`,
  not_for_us: ({ would }) => `Dismissing this${will(would)}`,
  rerun: ({ would }) => `Re-running${will(would)}`,
  reject: ({ record }) => `Closing ${record} as not an enquiry.`,
  reinstate: ({ record }) => `Reinstating ${record}.`,
  update: ({ record }) => `Adding this message to ${record} as an update.`,
  create: ({ label, would }) => `Creating a new ${label}${will(would)}`,
  accept_update: ({ record }) => `Accepting the update on ${record}.`,
  close: ({ record }) => `Closing ${record}.`,
}

/** Each action paired with its own words. A tuple union, so an action typed as
 * any action (not a literal) cannot pass the loosest action's words. */
type WorkingArgs = { [A in TriageAction]: [action: A, words: WorkingWords[A]] }[TriageAction]

/** SA-795 (D2): what the panel is doing while a request is held, built from
 * the server's own "would" sentence. No words are written per kind. */
export function workingLine(...args: WorkingArgs): string
export function workingLine<A extends TriageAction>(action: A, words: WorkingWords[A]): string {
  return WORKING[action](words) + MINUTE
}

export function intentLine(row: TriageRow): string | null {
  const words = row.replyIntent ? INTENT_TEXT.get(row.replyIntent) : undefined
  return words ? `Reads as ${words}.` : null
}

/** What the handler did, once the row is decided. */
export function outcomeLine(row: TriageRow): string | null {
  // A rejected row's headline already says what happened (SA-770).
  if (!row.handlerOutcome || row.decisionStatus === 'proposed' || row.handlerOutcome === 'rejected') return null
  const words = OUTCOME_WORDS[row.handlerOutcome] ?? 'Unknown outcome'
  return row.errorWords ? `${words}: ${row.errorWords}.` : `${words}.`
}

/** SA-1095: the lead above a waiting row's list of the thread's records. */
export function openRecordsLead(_noun: string, group: boolean): string {
  return group ? 'It replies to a message about' : 'This thread already has'
}

/** SA-1095: a thread record's state, lower case for a parenthetical. A
 * record closed with no outcome is a cancel, as the server words it
 * (`thread_records.outcome_words`). */
export function threadRecordState(record: ThreadRecord): string {
  if (record.state === 'open') return 'open'
  return outcomeWords(record.outcome) ?? 'cancelled'
}

/** SA-946 G2: the line closing a group's panel: the decided messages older
 * than the ten it shows. */
export function earlierLine(count: number): string {
  return `${count} earlier message${count === 1 ? '' : 's'} decided`
}

/** SA-1030 (spec D17, D18): the match block's own words; the server words each candidate. */
export const matchText = {
  heading: (noun: string) => `Is this an update to a ${noun} we already have?`,
  another: (noun: string) => `Another ${noun}`,
  anotherInput: (noun: string) => `${noun.charAt(0).toUpperCase()}${noun.slice(1)} number`,
  createChoice: (label: string) => `No, a new ${label}`,
  choose: 'Choose one',
  update: (number: string) => `Update ${number}`,
  close: (number: string) => `Close ${number}`,
  closeHeading: (noun: string) => `Which ${noun} does this answer?`,
  create: (label: string) => `Create new ${label}`,
  likely: 'likely',
  more: (count: number) => `and ${count} more`,
  noUpdateKind: (noun: string) =>
    `Add an update kind to this mailbox's policy to send a message to an existing ${noun}.`,
}

/** SA-1095 PR 2: the outcome select of a kind that closes the record a message answers. */
export const closeText = {
  label: (noun: string, number?: string | null) => (number ? `Close ${noun} ${number} as` : 'Close as'),
  nothing: 'Do not close',
}

/** SA-1095 (final review, ruling 3): Change kind's record pick, for a kind
 * that adds the message to a record on an email thread of several open ones. */
export const targetText = {
  label: (noun: string) => `Which ${noun}`,
  choose: (noun: string) => `Pick the ${noun}…`,
  /** SA-1229: the server wants a pick but the viewer sees no open record to offer. */
  noneVisible: (noun: string) => `No open ${noun} on this thread is one you can see.`,
}

/** SA-1034 PR 5 (spec §3.2): a foldable row's tip, by state. */
export const FOLD_TIP = { show: "Show this message's triage", hide: "Hide this message's triage" } as const

/** SA-946, SA-1295: a row's button that shows its message in the thread. */
export const SHOW_IN_THREAD = 'Show in thread'

/** SA-1295: an email row's text line when the email has no text of its own. */
export const NO_OWN_TEXT = 'No text of its own'
export const QUOTED_TEXT = 'quoted text'

/** The first two file names, then how many more. */
export function filesPart(names: readonly string[]): string {
  const shown = names.slice(0, 2).join(', ')
  return names.length > 2 ? `${shown} and ${names.length - 2} more` : shown
}

/** SA-1295: an email row's first line: sender, time, a differing subject. */
export function emailHeading(m: EmailRowMessage): string {
  return [m.sender, m.when, m.subject].filter((part): part is string => !!part).join(' · ')
}

/** SA-1295: an email row's accessible name: its two lines. */
export function emailRowName(m: EmailRowMessage): string {
  return `${emailHeading(m)} · ${m.text}`
}

/** SA-1295: a chip's short words for a decided row's outcome. */
const CHIP_OUTCOME: Record<string, string> = {
  done: 'Done',
  pending: 'Waiting on a follow-up',
  skipped: 'Nothing done',
  failed: 'Failed',
  running: 'Running',
}

/** SA-1295: what the chip on a triaged email says about its row. */
export function chipFor(row: TriageRow): { text: string; waiting: boolean } {
  if (row.handlerOutcome === 'rejected') return { text: OUTCOME_WORDS.rejected, waiting: false }
  if (row.decisionStatus === 'proposed') {
    const label = row.kind === 'unknown' ? (row.proposedLabel ?? 'a kind') : (row.kindLabel ?? row.kind ?? 'a kind')
    return { text: `Waiting: ${label}`, waiting: true }
  }
  const label = row.kindLabel ?? row.kind ?? 'Unknown'
  const outcome = row.handlerOutcome ? CHIP_OUTCOME[row.handlerOutcome] : undefined
  return { text: outcome ? `${label} · ${outcome}` : label, waiting: false }
}

/** SA-1295: the chip's accessible name: its visible words first (WCAG 2.5.3, SA-1300). */
export function chipName(text: string): string {
  return `${text}, show in triage`
}
/** SA-1295: the chip's tip. */
export const CHIP_TIP = "Show this email's triage"

// SA-1329: the triage block's words.

/** The AI's hand on a proposal: how it was made, marked. */
export const aiMark = (how: string | null): string | null => (how ? `✦ ${how}` : null)
export const NEEDS_A_KIND = 'Needs a kind'
export const closestText = (label: string) => `closest: ${label}`

export function needsChoiceText(choice: Exclude<NeedsChoice, null>, noun: string): string {
  return choice === 'record' ? `needs a ${noun} picked` : 'needs an outcome'
}
export const pickTargetLabel = (noun: string) => `Pick the ${noun}…`

/** A handler's `describe` sentence with the record noun put in. Every
 * handler words its record as the whole word "record", so this is the one
 * place that swaps it; the bulk confirm reuses it so both say the same. */
export function nounWould(would: string, noun: string): string {
  return would.replace(/\brecord\b/g, noun)
}

/** "Approving would <what>.", in parts so the block can set the phrase apart. */
export function consequenceParts(
  would: string | null | undefined, noun: string,
): { lead: string; phrase: string; end: string } | null {
  return would ? { lead: 'Approving would ', phrase: nounWould(would, noun), end: '.' } : null
}

export const WHY_LEAD = 'Why: '
export const REPLY_ON_THREAD = 'reply on this thread'
export const inReplyTo = (name: string) => `in reply to ${name}`

/** What is left of an email under its two lines: "3 more lines · 1 file". */
export function quoteFoot(moreLines: number, fileCount: number): string | null {
  const parts = [
    moreLines > 0 ? `${moreLines} more ${moreLines === 1 ? 'line' : 'lines'}` : null,
    fileCount > 0 ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'}` : null,
  ].filter((part): part is string => !!part)
  return parts.length ? parts.join(' · ') : null
}

export const waitingChipText = (n: number) => `${n} waiting`
export const messagesText = (n: number) => (n === 1 ? '1 message' : `${n} messages`)

/** A decided block's receipt: who decided, and when. */
export function receiptWords(row: TriageRow, now?: Date, zone?: string): string {
  const when = whenText(row.decidedAt, now, zone)
  const name = row.decidedByName
  let head: string
  if (row.handlerOutcome === 'rejected') head = `closed${by(name)}`
  else if (row.decisionStatus === 'approved') head = `approved${by(name)}`
  else if (row.decisionStatus === 'changed') head = `changed${by(name)}`
  else head = `decided automatically${how(row.decidedHow)}`
  return when ? `${head}, ${when}` : head
}

export const recordBandLead = (takesTarget: boolean) => (takesTarget ? 'On' : 'Made')
/** The record band's words (SA-1329): the line, the link and the two buttons. */
export const recordBandLine = (lead: string, number: string | null, noun: string, state: string | null) =>
  `${lead} ${number ?? `the ${noun}`}${state ? ` · ${state}` : ''}`
export const NOT_AN_ENQUIRY = 'Not an enquiry'
export const openRecordText = (noun: string, number: string | null) => `Open ${noun}${number ? ` ${number}` : ''}`
export const reinstateText = (noun: string, number: string | null) => `Reinstate ${number ?? noun}`
export const CLOSED_NOT_ENQUIRY = 'closed as not an enquiry'

// SA-1329: the selection's words (bulk decisions).
export const selectedText = (n: number, total: number) => `${n} of ${total} selected`
export const approveN = (n: number) => `Approve ${n}`
export const rerunN = (n: number) => `Re-run ${n}`
export const dismissN = (n: number) => `Dismiss ${n}`
export const changeKindN = (n: number) => `Change kind for ${n}…`
export const CLEAR = 'Clear'
export const KEEP_SELECTING = 'Keep selecting'
export const SELECT_A_KIND = 'Select a kind…'
export const APPLY = 'Apply'
export const KIND_LABEL = 'Kind'
export const SELECT_ALL = 'Select every message waiting'
export const selectLabel = (heading: string) => `Select ${heading}`

/** "Quote request (2), needing a kind (1), failed (1)"; the parts come in order. */
export function kindsSummary(parts: readonly (readonly [string, number])[]): string {
  return parts.map(([label, count]) => `${label} (${count})`).join(', ')
}
export const NEEDING_KIND = 'needing a kind'
export const FAILED_PART = 'failed'

export const approveReason = {
  record: (noun: string, n: number) => `Approve needs a ${noun} picked on ${n} of them`,
  outcome: (n: number) => `Approve needs an outcome on ${n} of them`,
  kind: (n: number) => `Approve needs a kind on ${n} of them`,
  rerun: (n: number) => `Approve needs a re-run on ${n} of them`,
  refused: 'Approve is refused on some of them.',
}
export const DISMISS_HELD = 'Dismiss can\'t be used on every selected message.'
export const rerunReason = (n: number) => `Re-run is refused on ${n} of them`
export const CHANGE_KIND_HELD = 'Change kind can\'t be used on every selected message.'

export const confirmHeading = (n: number) => `Approve ${n} ${n === 1 ? 'message' : 'messages'}?`
/** "Quote update (1) · quote CCA-1: approving would add this message to the quote." */
export function confirmLine(label: string, count: number, recordWords: readonly string[], would: string | null): string {
  const records = recordWords.length ? ` · ${recordWords.join(', ')}` : ''
  return `${label} (${count})${records}${would ? `: approving would ${would}.` : '.'}`
}
export const BULK_KIND_EXCLUDED = (noun: string) =>
  `Kinds that need a ${noun} picked or an outcome are decided one message at a time.`
export const NO_COMMON_KIND = 'The selected messages come from mailboxes with no kind in common.'

export const checkingText = (n: number) => `Checking ${n}…`
export const NOTHING_TO_DECIDE = 'nothing to decide'
export const NONE_CAN_RUN = 'None of the selected messages can be decided this way.'
export const NEEDS_KIND_REASON = 'needs a kind'
export const skippedText = (n: number, reason: string) => `${n} skipped: ${reason}`
export const approveOf = (n: number, total: number) => `Approve ${n} of ${total}`
export const runProgress = (done: number, total: number) => `${done} of ${total}…`
export const RUN_BUSY = 'A bulk decision is running.'
export const QUEUED = 'Queued'
export const DECIDING = 'Deciding…'

const RUN_VERB: Record<TriageRun['action'], string> = {
  approve: 'approved', change: 'changed', dismiss: 'dismissed', rerun: 're-run',
}

/** "2 approved · 1 changed meanwhile · 1 refused: Pick the record this answer closes." */
export function runEndText(run: TriageRun): string {
  const done = run.items.filter((item) => item.state === 'done').length
  const conflict = run.items.filter((item) => item.state === 'conflict').length
  const refused = run.items.filter((item) => item.state === 'refused')
  const parts: string[] = []
  if (done) parts.push(`${done} ${RUN_VERB[run.action]}`)
  if (conflict) parts.push(`${conflict} changed meanwhile`)
  if (refused.length) {
    const first = refused.find((item) => item.sentence)?.sentence
    parts.push(`${refused.length} refused${first ? `: ${first}` : ''}`)
  }
  return parts.join(' · ')
}

/** SA-1329: a panel block whose message was decided by someone else while a run reached it. */
export const ROW_CHANGED_MEANWHILE = 'Someone else changed this message meanwhile.'

/** SA-1411: in place of Approve on a message whose proposed kind left the mailbox's policy. */
export const KIND_REMOVED_LINE = 'This kind is no longer in the policy. Change the kind to decide it.'
